<template>
  <v-skeleton-loader
    :loading="skeleton"
    :type="skeletonType"
  >
    <component
      :is="componentInner"
      v-if="disabled"
      ref="input"
      v-bind="$attrs"
      :value="computedValue"
      :label="label"
      :name="name"
      :readonly="readonly"
      disabled
      hide-details
      outlined
      dense
      class="b-text-field"
    >
      <template #append>
        <slot name="info" />
      </template>
    </component>
    <component
      :is="componentInner"
      v-else
      ref="input"
      v-validate="validateCustom"
      v-bind="$attrs"
      :value="value"
      :label="label"
      :name="name"
      hide-details="auto"
      :readonly="readonly"
      :placeholder="placeholder"
      :error="$errors.has(name)"
      :error-messages="errorIcon ? null : $errors.first(name)"
      :data-vv-as="(dataVvAs || label) ? `«${dataVvAs || label}»` : ' '"
      :clearable="clearable"
      :counter="counter"
      :autocomplete="autocomplete"
      outlined
      dense
      class="b-text-field"
      v-on="listeners"
      @input="$errors.remove(name)"
    >
      <template v-if="required" #label>
        <template v-if="label">
          {{ label }}*
        </template>
      </template>

      <template v-for="(_, key) in $slots" #[key]>
        <slot :name="key" />
      </template>

      <template v-for="(_, key) in $scopedSlots" #[key]="props">
        <slot v-bind="props" :name="key" />
      </template>

      <template #append>
        <slot name="info" />

        <BInfo
          v-if="errorIcon && $errors.has(name)"
          color="error"
          size="20px"
          icon="mdi-alert-circle-outline"
          icon-class="b-text-field__error-icon"
          :text="$errors.first(name)"
        />
        <slot v-else name="append" />
      </template>
    </component>
  </v-skeleton-loader>
</template>

<script>
import uniqueId from 'lodash/uniqueId'
import { DEFAULT_TEXT_FILED_MAX_LENGTH } from '@/helpers/enums/params'

import BInfo from '@/components/common/BInfo.vue'
import { VTextField } from 'vuetify/lib/components/VTextField'
import { isNumber } from 'lodash'


export default {
  name: 'BTextField',
  inject: ['$validator'],
  components: {
    BInfo,
  },
  inheritAttrs: false,
  props: {
    value: null,
    skeleton: Boolean,
    validate: [String, Object],
    dataVvAs: String,
    counter: Boolean,
    component: null,
    errorIcon: Boolean,
    readonly: Boolean,
    maxCounter: {
      type: Number,
      default: DEFAULT_TEXT_FILED_MAX_LENGTH,
      validator: (prop) => prop >= 0,
    },
    label: String,
    placeholder: String,
    autocomplete: {
      type: String,
      default: 'nope',
      validator: (prop) => ['on', 'off', 'nope'].includes(prop),
    },
    name: {
      type: String,
      default: () => uniqueId('b-text-field-'),
    },
    disabled: Boolean,
    alwaysClearable: Boolean,
  },
  data() {
    return {
      skeletonType: this.$attrs.skeletonType || 'sentences',
      componentInner: this.component || VTextField,
      listeners: this.$listeners || Object.freeze({}),
    }
  },
  computed: {
    computedValue() {
      if (isNumber(this.value)) {
        return this.value
      }
      return this.value || '---'
    },
    required() {
      const { validate } = this

      if (typeof validate === 'string') {
        return validate.includes('required')
      }

      return this.validate?.required
    },
    clearable() {
      if (this.alwaysClearable) {
        return true
      } if (this.readonly) {
        return false
      }
      return 'clearable' in this.$attrs ? this.$attrs.clearable : !this.required
    },
    validateCustom() {
      const { maxCounter, validate } = this
      if (!maxCounter) return

      const { type } = this.$attrs
      // eslint-disable-next-line consistent-return
      if (type && type !== 'text') return validate

      switch (typeof validate) {
        case 'undefined':
          // eslint-disable-next-line consistent-return
          return { max: maxCounter }

        case 'object':
          // eslint-disable-next-line consistent-return
          return { max: maxCounter, ...validate }

        case 'string':
          // eslint-disable-next-line consistent-return
          return `max:${maxCounter}|${validate}`

        default:
          // eslint-disable-next-line consistent-return
          return validate
      }
    },
  },
}
</script>

<style lang="scss">
$use-shake: true;
$base-distance: 4px;

.b-text-field {
  &.v-input--is-disabled input {
    color: $color-black;
  }

  &.v-text-field.v-text-field--enclosed .v-text-field__details {
    padding: 0 2px;
    margin-bottom: 0;
    font-size: 10px;
  }

  &__error-icon {
    @include animation(shake, 4px);
  }
}
</style>
